/* eslint-disable max-statements */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { OrderBulkDto, OrderFulfillmentsDto } from '@tiendanube/common';
import {
  Action,
  FulfillmentPreferenceType as FulfillmentPreferenceTypeEnum,
} from '@tiendanube/common/src/enums';
import { Responsive } from 'commons/components';
import { useModal } from 'commons/hooks';
import { openWindow } from 'commons/utils/window';
import { useBulkActions } from 'domains/Catalog/Products/pages/ProductListPage/components';
import { DocumentType } from 'domains/FulfillmentOrders/pages/PrintDocumentPage/constants';
import { useGoToPrintPage } from 'domains/FulfillmentOrders/pages/PrintDocumentPage/hooks';
import ModalOrdersAssignMetafields from 'domains/Metafields/components/ModalOrdersAssignMetafields';
import { trackingOrderBulkMetafieldsCancel } from 'domains/Metafields/components/ModalOrdersAssignMetafields/tracking';
import { NE_PRINT_LABEL_SOURCE } from 'domains/NuvemEnvioCommons/constants';
import { OrdersPrintLabelModal } from 'domains/NuvemEnvioCommons/Orders/components/PrintLabelModal';
import useNuvemEnvioPrintLabel from 'domains/NuvemEnvioCommons/Orders/hooks/useNuvemEnvioPrintLabel/useNuvemEnvioPrintLabel';
import {
  isNuvemEnvioLink,
  transformBulkOrdersToOrdersForPrintLabel,
} from 'domains/NuvemEnvioCommons/Orders/utils';
import { useOrdersList } from 'domains/Orders/Orders/hooks';
import { useIsOrdersBulkPickupEnabled } from 'domains/Orders/Orders/hooks/useIsOrdersBulkPickupEnabled';
import useSelectBulkOrders from 'domains/Orders/Orders/hooks/useSelectBulkOrders';
import { ActionType } from 'domains/Orders/Orders/ordersService';
import {
  getBulkOrders,
  getOrdersShippingMethods,
} from 'domains/Orders/Orders/ordersSlice';
import { ORDERS_PER_PAGE } from 'domains/Orders/Orders/pages/constants';
import {
  fulfillmentOrdersFromSelectedOrderIds,
  hasOca,
  shippingMethodFromOrdersBySelectedOrderIds,
} from 'domains/Orders/Orders/utils/OrderListFilter/utils';
import { useAppsLinks } from 'domains/PartnersApps/hooks';
import { OrderResultsDesktop, OrderResultsMobile } from './components';
import { useHasPermission } from '../../../../../../Auth/hooks';
import { useBulkMetafieldsAssignment } from '../../../hooks';
import FulfillOrdersModal from '../FulfillOrdersModal';
import OrderCancelModal from '../OrderCancelModal';
import { OrderMarkAsPaidModalAside } from '../OrderMarkAsPaidModalAside';

interface OrderListResultsProps {
  isEditMode: boolean;
  hasOrder: boolean;
  isLoadingHasOrders: boolean;
  onToggleEditMode: () => void;
  onFilterLinkClick: () => void;
}

function OrderListResults({
  isEditMode,
  hasOrder,
  isLoadingHasOrders,
  onToggleEditMode,
  onFilterLinkClick,
}: OrderListResultsProps): JSX.Element {
  const { filters, updateOrders } = useOrdersList();
  const { generateBulkAppLink } = useAppsLinks();
  const {
    printNuvemEnvioLabelsFromOrdersList,
    showNuvemEnvioPrintLabelModal,
    closeNuvemEnvioPrintLabelModal,
  } = useNuvemEnvioPrintLabel();

  const [showCancelModal, openCancelModal, closeCancelModal] = useModal();
  const [showFulfillModal, openFulfillModal, closeFulfillModal] = useModal();

  const [showMarkAsPaidModal, openMarkAsPaidModal, closeMarkAsPaidModal] =
    useModal();
  const [
    showBulkMetafieldsModal,
    openBulkMetafieldsModal,
    closeBulkMetafieldsModal,
  ] = useModal();
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const { setDisabled } = useBulkActions();
  const {
    handleDeselect,
    missingTrackingNumberCount,
    cleanOrdersMissingTrackingNumber,
    hasFulfillments,
  } = useSelectBulkOrders(filters, setDisabled);
  const bulkOrders = useSelector(getBulkOrders);
  const ordersShippingMethods = useSelector(getOrdersShippingMethods);
  const [packagesCount, setPackagesCount] = useState(0);
  const [fulfillmentsForModal, setFulfillmentsForModal] = useState<
    OrderFulfillmentsDto[]
  >([]);
  const [ordersForPaymentModal, setOrdersForPaymentModal] = useState<
    OrderBulkDto[]
  >([]);
  const { isLoading: isBulkAssignMetafieldsLoading } =
    useBulkMetafieldsAssignment();
  const { goToPrintPage } = useGoToPrintPage();
  const isOrdersBulkPickupEnabled = useIsOrdersBulkPickupEnabled();
  const hasPrintShippingLabelPermission = useHasPermission(
    'print_order_shipping_labels',
  );

  function getPackagesCount(ordersWithFulfillments: OrderFulfillmentsDto[]) {
    return ordersWithFulfillments.reduce(
      (acc, { fulfillments }) => acc + (fulfillments.length || 1),
      0,
    );
  }
  const filterNonShippableFulfillments = (
    fulfillmentOrders: OrderFulfillmentsDto[],
  ) =>
    fulfillmentOrders.filter(({ fulfillments }) =>
      fulfillments.every(
        ({ shippingType }) => shippingType !== 'non-shippable',
      ),
    );

  const filterNonPickupableFulfillments = (
    fulfillmentOrders: OrderFulfillmentsDto[],
  ) =>
    fulfillmentOrders.filter(({ fulfillments }) =>
      fulfillments.every(
        ({ shippingType }) =>
          shippingType !== FulfillmentPreferenceTypeEnum.PICKUP,
      ),
    );

  const setPackagesAndFulfillmentsForModal = (
    orders: OrderFulfillmentsDto[],
  ) => {
    setPackagesCount(getPackagesCount(orders));
    setFulfillmentsForModal(orders);
    handleDeselect();
  };

  const handleBulkAction = async (ids: string[], value: Action) => {
    if ([Action.PACK, Action.ARCHIVE].includes(value)) {
      onToggleEditMode();
      const isPack = value === Action.PACK;
      const payload = {
        actionType: value,
        orderIds: ids,
        action: isPack
          ? filterNonShippableFulfillments(
              fulfillmentOrdersFromSelectedOrderIds(ids, bulkOrders),
            )
          : [],
      };
      await updateOrders(payload);
      onToggleEditMode();
      if (ids.length > ORDERS_PER_PAGE) handleDeselect();
    } else if (value === Action.FULFILL) {
      // Avoid orders that are pickupable. Use this anti-flag to enable/disable this feature.
      if (!isOrdersBulkPickupEnabled) {
        const nonPickupableFulfillments = filterNonPickupableFulfillments(
          fulfillmentOrdersFromSelectedOrderIds(ids, bulkOrders),
        );
        if (nonPickupableFulfillments.length > 0) {
          setSelectedOrderIds(
            nonPickupableFulfillments.map(({ orderId }) => orderId),
          );
          if (ids.length > ORDERS_PER_PAGE) {
            setPackagesAndFulfillmentsForModal(nonPickupableFulfillments);
          }
        }
        // If the feature is enabled, should open the modal with all the orders as a normal behavior.
      } else {
        setSelectedOrderIds(ids);
        if (ids.length > ORDERS_PER_PAGE) {
          setPackagesAndFulfillmentsForModal(bulkOrders);
        }
      }
      openFulfillModal();
    } else if (value === Action.PAY) {
      setSelectedOrderIds(ids);
      setOrdersForPaymentModal(bulkOrders);
      openMarkAsPaidModal();
      if (ids.length > ORDERS_PER_PAGE) {
        handleDeselect();
      }
    } else if (value === Action.CANCEL) {
      setSelectedOrderIds(ids);
      openCancelModal();
      if (ids.length > ORDERS_PER_PAGE) handleDeselect();
    } else if (value === Action.PRINT_RECEIPT) {
      goToPrintPage({ ids, type: DocumentType.RECEIPT });
    } else if (
      value === Action.PRINT_PACKING_LABEL ||
      value === Action.PRINT_OCA_LABEL
    ) {
      const ordersShippingMethodsFromSelectedOrders =
        shippingMethodFromOrdersBySelectedOrderIds(ids, ordersShippingMethods);

      goToPrintPage({
        ids: ids,
        type: DocumentType.LABEL,
        hasOca: ordersShippingMethodsFromSelectedOrders.some(hasOca),
        hasOcaAndNotGeneratedTag: true,
      });
    } else if (value === Action.PRINT_SHIPPING_DOCUMENT) {
      goToPrintPage({ ids, type: DocumentType.SHIPPING_DOCUMENT });
    } else if (value === Action.PRINT_CONTENT_DECLARATION) {
      goToPrintPage({ ids, type: DocumentType.CONTENT_DECLARATION });
    } else if (value === Action.ASSIGN_METAFIELDS) {
      setSelectedOrderIds(ids);
      openBulkMetafieldsModal();
    } else if (isNuvemEnvioLink(value)) {
      setSelectedOrderIds(ids);
      const ordersForPrint = transformBulkOrdersToOrdersForPrintLabel(
        ids,
        bulkOrders,
      );
      printNuvemEnvioLabelsFromOrdersList(ordersForPrint);
    } else {
      openWindow(generateBulkAppLink(value, ids), true);
    }
  };

  const onCloseFulfillModal = () => {
    closeFulfillModal();
    cleanOrdersMissingTrackingNumber();
  };

  const onCloseMarkAsPaidModal = () => {
    closeMarkAsPaidModal();
    setOrdersForPaymentModal([]);
  };

  const onCloseBulkMetafields = () => {
    closeBulkMetafieldsModal();
    trackingOrderBulkMetafieldsCancel(selectedOrderIds);
  };

  const handleOnFulfill = async (action: ActionType) => {
    setIsActionLoading(true);
    await updateOrders({
      actionType: Action.FULFILL,
      orderIds: selectedOrderIds,
      action,
    });
    onToggleEditMode();
    setIsActionLoading(false);
    onCloseFulfillModal();
    setFulfillmentsForModal([]);
  };

  const handleMarkAsPaid = async (orderIdsToPay: string[]) => {
    setIsActionLoading(true);
    await updateOrders({
      actionType: Action.PAY,
      orderIds: orderIdsToPay,
    });
    onToggleEditMode();
    setIsActionLoading(false);
    closeMarkAsPaidModal();
    setOrdersForPaymentModal([]);
  };

  const handleOnCancel = async (action: ActionType) => {
    setIsActionLoading(true);
    await updateOrders({
      actionType: Action.CANCEL,
      orderIds: selectedOrderIds,
      action,
    });
    onToggleEditMode();
    setIsActionLoading(false);
    closeCancelModal();
  };

  return (
    <>
      <Responsive
        mobile={
          <OrderResultsMobile
            hasOrder={hasOrder}
            isLoadingHasOrders={isLoadingHasOrders}
            isEditMode={isEditMode}
            onToggleEditMode={onToggleEditMode}
            onHandleBulkAction={handleBulkAction}
          />
        }
        desktop={
          <OrderResultsDesktop
            hasOrder={hasOrder}
            isLoadingHasOrders={isLoadingHasOrders}
            onHandleBulkAction={handleBulkAction}
            onFilterLinkClick={onFilterLinkClick}
          />
        }
      />
      <OrderCancelModal
        show={showCancelModal}
        hideModal={closeCancelModal}
        orderIds={selectedOrderIds}
        appliedAction={handleOnCancel}
        isLoading={isActionLoading}
      />
      {showMarkAsPaidModal && (
        <OrderMarkAsPaidModalAside
          isOpen={showMarkAsPaidModal}
          orderIds={selectedOrderIds}
          orders={ordersForPaymentModal}
          onConfirm={handleMarkAsPaid}
          onClose={onCloseMarkAsPaidModal}
        />
      )}
      {showFulfillModal && (
        <FulfillOrdersModal
          show={showFulfillModal}
          orderIds={selectedOrderIds}
          isLoading={isActionLoading}
          onConfirm={handleOnFulfill}
          onClose={onCloseFulfillModal}
          ordersMissingTracking={missingTrackingNumberCount}
          packagesCount={packagesCount}
          hasFulfillments={hasFulfillments}
          fulfillmentsForModal={fulfillmentsForModal}
          isSingleOrder={false}
        />
      )}

      {hasPrintShippingLabelPermission && showNuvemEnvioPrintLabelModal && (
        <OrdersPrintLabelModal
          onClose={closeNuvemEnvioPrintLabelModal}
          selectedRowsId={selectedOrderIds}
          bulkOrders={bulkOrders}
          source={NE_PRINT_LABEL_SOURCE.ORDERS_LIST}
        />
      )}

      <ModalOrdersAssignMetafields
        handleOnClose={onCloseBulkMetafields}
        isLoading={isBulkAssignMetafieldsLoading}
        selectedRowsId={selectedOrderIds}
        show={showBulkMetafieldsModal}
        onToggleEditMode={onToggleEditMode}
      />
    </>
  );
}

export default OrderListResults;
